import type React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { HandlerScope } from "../../types/handler";
import { handlerName } from "../../types/handler";
import { useHandlers } from "../../hooks/useHandlers";
import { HandlerBubble } from "./HandlerBubble";

/**
 * ── Mine / Everyone / a person ───────────────────────────────────────────────
 *
 * One segmented control, in the toolbar of every screen that lists transports.
 * Two of the three answers are a single press, because they are the two that
 * are pressed: "show me my work" and "show me all of it". The third opens the
 * roster, for the less common question of what a colleague is carrying — when
 * somebody is off, or when a client rings and you need to know who to fetch.
 *
 * ── It is a lens, not a gate ─────────────────────────────────────────────────
 *
 * Nobody is locked out of anything by setting this. Everyone keeps the ability
 * to see, open and change every transport in the tenant; the only thing that
 * changes is which rows are drawn. That is why "Everyone" is always one press
 * away and why it is where every screen starts.
 *
 * ── No counts in the roster ──────────────────────────────────────────────────
 *
 * Deliberately. A count beside each name would have to be computed by the same
 * predicate the filter uses — which on the orders list means a two-stage query
 * per person, since an order matches through its containers as well as its own
 * handler — and twelve of those recomputed each time the menu opens buys a
 * number nobody is deciding anything on. The menu's job is to get you to a
 * person; the screen behind it already says how much they have.
 */

interface HandlerScopeFilterProps {
  scope: HandlerScope;
  onChange: (next: HandlerScope) => void;
  /** Hidden entirely when a tenant has nobody to filter by. */
  className?: string;
}

const MENU_WIDTH = 248;
const MENU_GAP = 6;
const VIEWPORT_MARGIN = 8;

export const HandlerScopeFilter: React.FC<HandlerScopeFilterProps> = ({
  scope,
  onChange,
  className = "",
}) => {
  const { t } = useTranslation("containerOrders");
  const { handlers, byId, meId, ready } = useHandlers();

  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const left = Math.min(
        Math.max(VIEWPORT_MARGIN, rect.right - MENU_WIDTH),
        window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN,
      );
      const estimated = Math.min(320, 44 * (handlers.length + 1) + 16);
      const below = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      const top =
        below < estimated && rect.top > below
          ? rect.top - estimated - MENU_GAP
          : rect.bottom + MENU_GAP;
      setCoords({ top, left });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, handlers.length]);

  useLayoutEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  /**
   * The control is not drawn at all for a one-person office.
   *
   * With nobody to compare against, every scope answers the same question and
   * the only thing three extra buttons can do is take up the toolbar and invite
   * somebody to filter themselves into an empty screen. Rendered once the
   * roster has landed, so it does not flash in and out on load.
   */
  if (!ready || handlers.length < 2) return null;

  const person =
    scope !== "all" && scope !== "mine" && scope !== "unassigned"
      ? byId(scope)
      : null;
  const personSelected = scope === "unassigned" || !!person;

  const segment =
    "flex items-center gap-1.5 border-r border-[rgba(11,15,14,0.12)] px-2.5 py-1.5 text-[12px] font-semibold transition-colors last:border-r-0";
  const on = "bg-ink text-white";
  const off = "bg-white text-secondary-700 hover:bg-bone";

  const pick = (next: HandlerScope) => {
    setOpen(false);
    onChange(next);
  };

  return (
    <>
      <div
        role="group"
        aria-label={t("handler.filter.label")}
        className={`inline-flex overflow-hidden rounded-md border border-[rgba(11,15,14,0.15)] ${className}`}
      >
        <button
          type="button"
          onClick={() => onChange("mine")}
          aria-pressed={scope === "mine"}
          className={`${segment} ${scope === "mine" ? on : off}`}
        >
          <HandlerBubble
            handler={byId(meId)}
            // No ring inside this button: the whole segment is already saying
            // "you", and a ring on a dark fill reads as a selection artefact.
            size="sm"
            silent
          />
          {t("handler.filter.mine")}
        </button>

        <button
          type="button"
          onClick={() => onChange("all")}
          aria-pressed={scope === "all"}
          className={`${segment} ${scope === "all" ? on : off}`}
        >
          {t("handler.filter.everyone")}
        </button>

        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-haspopup="menu"
          aria-expanded={open}
          className={`${segment} ${personSelected ? on : off}`}
        >
          {person ? (
            <>
              <HandlerBubble handler={person} size="sm" silent />
              <span className="max-w-[90px] truncate">
                {person.firstName || handlerName(person)}
              </span>
            </>
          ) : scope === "unassigned" ? (
            <>
              <HandlerBubble handler={null} size="sm" silent />
              {t("handler.filter.unassigned")}
            </>
          ) : (
            <>
              <Users className="h-3.5 w-3.5" />
              {t("handler.filter.person")}
            </>
          )}
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>

      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: coords.top, left: coords.left, width: MENU_WIDTH }}
            className="fixed z-70 max-h-80 overflow-y-auto rounded-lg border border-[rgba(11,15,14,0.15)] bg-white p-1 shadow-xl"
          >
            {/* First, because it is the one people come here for: the backlog
                of work nobody has picked up. */}
            <button
              type="button"
              role="menuitem"
              onClick={() => pick("unassigned")}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-bone"
            >
              <HandlerBubble handler={null} size="sm" silent />
              <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-gray-700">
                {t("handler.filter.unassigned")}
              </span>
              {scope === "unassigned" && (
                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
              )}
            </button>

            <div className="my-1 border-t border-[rgba(11,15,14,0.08)]" />

            {handlers.map((candidate) => (
              <button
                key={candidate._id}
                type="button"
                role="menuitem"
                onClick={() => pick(candidate._id)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-bone"
              >
                <HandlerBubble
                  handler={candidate}
                  meId={meId}
                  size="sm"
                  silent
                />
                <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-gray-700">
                  {handlerName(candidate)}
                </span>
                {candidate._id === meId && (
                  <span className="shrink-0 text-[10px] text-mist">
                    {t("handler.you")}
                  </span>
                )}
                {scope === candidate._id && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default HandlerScopeFilter;
